/**
 * Reads consent decisions out of DataGrail (CA-5727).
 *
 * DataGrail delivers preferences through a queue rather than DOM events: handlers are
 * pushed onto `window.dgEvent` and fire whether or not `consent.js` has loaded yet, so
 * callers don't have to win a race.
 *
 * Two events matter. `initial_preference_callback` reports whatever is already on file
 * when the page loads, and `preference_callback` reports a change made during the
 * session. Both are registered so a returning visitor and someone who decides
 * mid-session are handled the same way.
 *
 * This mirrors `app/_utils/datagrailConsent.ts` in collective-landing. The two copies
 * are deliberate — there is no shared package — so changes belong in both.
 */

export type ConsentCategory = 'essential' | 'performance' | 'functional' | 'marketing';

/** DataGrail spells the categories `dg-category-<name>` in the payload it hands back. */
const categoryKey = (category: ConsentCategory): string => `dg-category-${category}`;

type CookieOption = {
    gtm_key?: string;
    isEnabled?: boolean;
};

/**
 * What DataGrail hands both callbacks. Shape read from `consent.js`: `action` is `page_load` for
 * the initial report and the button pressed (`accept_all`, `essential_only`, `custom`) for a change.
 */
export type ConsentPayload = {
    consentPreferences?: {
        cookieOptions?: CookieOption[];
        /** British spelling, as DataGrail writes it. */
        isCustomised?: boolean;
    };
    action?: string;
    policyName?: string;
    localeCode?: string;
};

/**
 * Whether the visitor has made a choice, as opposed to carrying their policy's defaults.
 *
 * `consent.js` sets `isCustomised` only when its `datagrail_consent_preferences` cookie exists,
 * which it writes on an explicit accept, essential-only, or custom save.
 */
export const hasDecided = (payload: ConsentPayload | undefined): boolean =>
    payload?.consentPreferences?.isCustomised === true;

type DgEventEntry = {
    event: 'initial_preference_callback' | 'preference_callback';
    params: (payload: ConsentPayload) => void;
};

declare global {
    interface Window {
        /** Absent until DataGrail is delivered to the page, so callers must guard. */
        dgEvent?: DgEventEntry[];
    }
}

/**
 * Whether `category` is granted in a DataGrail preference payload.
 *
 * A category missing from `cookieOptions` is treated as denied rather than unknown: the
 * payload is meant to list every category, so an absent one is more likely a renamed or
 * removed category than an implicit grant, and denying is the safer reading.
 */
export const isCategoryEnabled = (payload: ConsentPayload | undefined, category: ConsentCategory): boolean => {
    const options = payload?.consentPreferences?.cookieOptions;
    if (!Array.isArray(options)) {
        return false;
    }

    const match = options.find((option) => option?.gtm_key === categoryKey(category));
    return match?.isEnabled === true;
};

/**
 * Calls `callback` with DataGrail's full payload on page load, then again on every change,
 * including one made from a "Your privacy choices" link, which reopens the same banner.
 *
 * Returns a teardown that stops further calls. DataGrail exposes no way to pop a handler off
 * `window.dgEvent`, so the handler stays registered and is neutered by a flag.
 */
export const onPreferences = (callback: (payload: ConsentPayload) => void): (() => void) => {
    if (typeof window === 'undefined') {
        return () => {};
    }

    let active = true;

    const handler = (payload: ConsentPayload) => {
        if (!active) {
            return;
        }
        callback(payload);
    };

    window.dgEvent = window.dgEvent || [];
    window.dgEvent.push({ event: 'initial_preference_callback', params: handler });
    window.dgEvent.push({ event: 'preference_callback', params: handler });

    return () => {
        active = false;
    };
};

/**
 * Calls `callback` with the current grant state for `category`, then again whenever the
 * visitor changes it.
 *
 * Returns a teardown that stops further calls. DataGrail exposes no way to pop a handler
 * off `window.dgEvent`, so the handler stays registered and is neutered by a flag — which
 * is what keeps it from calling `setState` on an unmounted component.
 */
export const onConsent = (category: ConsentCategory, callback: (allowed: boolean) => void): (() => void) =>
    onPreferences((payload) => callback(isCategoryEnabled(payload, category)));

/** Whether a click is already waiting for DataGrail, so repeated clicks open the banner once. */
let isOpenPending = false;

/**
 * Opens the DataGrail consent banner now, or as soon as `consent.js` loads (CA-5726).
 *
 * Callers render from the Statsig gate, which says nothing about whether DataGrail has
 * arrived yet, so a click before then would otherwise be lost. `initial_preference_callback`
 * doubles as the readiness signal: DataGrail runs queued handlers once it loads.
 */
export const openConsentBanner = (): void => {
    if (typeof window === 'undefined') {
        return;
    }

    if (window.DG_BANNER_API) {
        // The API can exist before DataGrail drains the queue, so a queued open still pending
        // would reopen the banner after this one. Opening now satisfies it.
        isOpenPending = false;
        window.DG_BANNER_API.showConsentBanner();
        return;
    }

    if (isOpenPending) {
        return;
    }
    isOpenPending = true;

    window.dgEvent = window.dgEvent || [];
    window.dgEvent.push({
        event: 'initial_preference_callback',
        params: () => {
            if (!isOpenPending) {
                return;
            }
            isOpenPending = false;
            window.DG_BANNER_API?.showConsentBanner();
        },
    });
};
